import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DonationStepLayout } from '../../DonationStepLayout.js';
import type { UsePhoneFirstDonationJourneyResult } from '../../hooks/usePhoneFirstDonationJourney.js';
import {
  PhoneFirstDonationAmountStepView,
  resolveDonationAmountTitle,
} from './PhoneFirstDonationAmountStepView.js';
import {
  donationProjectSubtitle,
  PhoneFirstDonationProjectStepView,
} from './PhoneFirstDonationProjectStepView.js';
import { PhoneFirstDonationMethodStepView } from './PhoneFirstDonationMethodStepView.js';
import { PhoneFirstDonationPayStepView } from './PhoneFirstDonationPayStepView.js';
import { resolvePostAuthFallbackPath } from '../../../../navigation/navigationEngine.js';
import {
  usePublishHierarchicalBackFlow,
  type HierarchicalBackFlowPublishInput,
} from '../../../../shared/context/HierarchicalBackFlowContext.js';

export type PhoneFirstDonationScreenViewProps = UsePhoneFirstDonationJourneyResult;

type DonationStepBack = {
  readonly backTo?: string | null;
  readonly onBack?: () => void;
  readonly backLabel?: string;
};

/**
 * Locked chain (OS + in-app): pay → method → project → amount;
 * guest amount → shop; account donate tab root → no ←.
 *
 * HierarchicalBackFlow publish exclusivity:
 * - Cross-route (amount→shop, project→amount): `stepBackTo` only, `onStepBack: null`.
 * - Same-URL (method→project on /donate/projects, pay→method, legacy method→amount):
 *   `onStepBack` only, `stepBackTo: null`.
 * Content-row DonationStepLayout/JourneyShell `onBack`/`backTo` mirror those parents for
 * shellOwnsBack=false / non-shell fallback; under CustomerAppShell, W2
 * CustomerSubpageBackBar suppress hides them so fixed header owns the single ←.
 * Width: DonationStepLayout applies CUSTOMER_CONTENT_NARROW_CLASS (guest + embedded).
 */
export function PhoneFirstDonationScreenView(
  props: PhoneFirstDonationScreenViewProps,
): JSX.Element | null {
  const {
    t,
    visibleStep,
    embeddedInAccount,
    amountMinor,
    numberLocale,
    tenantCode,
    hasLegacyCode,
    setStep,
    donatePath,
    paymentId,
  } = props;
  const { i18n } = useTranslation('customer');
  const shopExit = resolvePostAuthFallbackPath(tenantCode);

  /** Legacy method→amount: same donate/:code URL. */
  const onBackFromMethodLegacy = useCallback(() => {
    setStep('amount');
  }, [setStep]);

  /** Non-legacy method→project: same /donate/projects URL. */
  const onBackFromMethodToProject = useCallback(() => {
    setStep('project');
  }, [setStep]);

  const onBackFromPay = useCallback(() => {
    setStep('method');
  }, [setStep]);

  const back = useMemo((): DonationStepBack => {
    if (visibleStep === 'amount') {
      if (embeddedInAccount) {
        return { backTo: null };
      }
      return { backTo: shopExit, backLabel: t('checkout.success.backToShop') };
    }
    if (visibleStep === 'project') {
      // Cross-route named parent — matches OS stepBackTo (like checkout collect).
      return { backTo: donatePath, backLabel: t('donation.back') };
    }
    if (visibleStep === 'method') {
      if (hasLegacyCode) {
        return { onBack: onBackFromMethodLegacy, backLabel: t('donation.back') };
      }
      return { onBack: onBackFromMethodToProject, backLabel: t('donation.back') };
    }
    if (visibleStep === 'pay') {
      return { onBack: onBackFromPay, backLabel: t('donation.back') };
    }
    return { backTo: null };
  }, [
    visibleStep,
    embeddedInAccount,
    shopExit,
    donatePath,
    hasLegacyCode,
    t,
    onBackFromMethodLegacy,
    onBackFromMethodToProject,
    onBackFromPay,
  ]);

  const publishInput = useMemo((): HierarchicalBackFlowPublishInput | null => {
    if (
      visibleStep !== 'amount' &&
      visibleStep !== 'project' &&
      visibleStep !== 'method' &&
      visibleStep !== 'pay'
    ) {
      return null;
    }
    // Account donate amount root: no ← — do not publish a step-back handler.
    if (visibleStep === 'amount' && embeddedInAccount) {
      return null;
    }
    if (visibleStep === 'amount') {
      return {
        flowKind: 'donation',
        flowStep: 'amount',
        paymentActive: false,
        stepBackTo: shopExit,
        onStepBack: null,
      };
    }
    if (visibleStep === 'project') {
      return {
        flowKind: 'donation',
        flowStep: 'project',
        paymentActive: false,
        stepBackTo: donatePath,
        onStepBack: null,
      };
    }
    if (visibleStep === 'method') {
      if (hasLegacyCode) {
        return {
          flowKind: 'donation',
          flowStep: 'method',
          paymentActive: false,
          stepBackTo: null,
          onStepBack: onBackFromMethodLegacy,
        };
      }
      return {
        flowKind: 'donation',
        flowStep: 'method',
        paymentActive: false,
        stepBackTo: null,
        onStepBack: onBackFromMethodToProject,
      };
    }
    return {
      flowKind: 'donation',
      flowStep: 'pay',
      paymentActive: true,
      stepBackTo: null,
      onStepBack: onBackFromPay,
    };
  }, [
    visibleStep,
    embeddedInAccount,
    shopExit,
    donatePath,
    hasLegacyCode,
    onBackFromMethodLegacy,
    onBackFromMethodToProject,
    onBackFromPay,
  ]);

  usePublishHierarchicalBackFlow(publishInput);

  if (visibleStep === 'amount') {
    return (
      <DonationStepLayout
        title={resolveDonationAmountTitle({ ...props, language: i18n.language })}
        subtitle={t('donation.enterAmount')}
        embeddedInAccount={embeddedInAccount}
        {...back}
      >
        <PhoneFirstDonationAmountStepView {...props} />
      </DonationStepLayout>
    );
  }

  if (visibleStep === 'project') {
    return (
      <DonationStepLayout
        title={t('donation.selectProject')}
        subtitle={donationProjectSubtitle(amountMinor, numberLocale)}
        embeddedInAccount={embeddedInAccount}
        {...back}
      >
        <PhoneFirstDonationProjectStepView {...props} />
      </DonationStepLayout>
    );
  }

  if (visibleStep === 'method') {
    return (
      <DonationStepLayout
        title={t('donation.paymentMethod')}
        subtitle={donationProjectSubtitle(amountMinor, numberLocale)}
        embeddedInAccount={embeddedInAccount}
        {...back}
      >
        <PhoneFirstDonationMethodStepView {...props} />
      </DonationStepLayout>
    );
  }

  if (visibleStep === 'pay' && paymentId !== null) {
    return (
      <DonationStepLayout
        title={t('donation.paymentInProgress')}
        subtitle={donationProjectSubtitle(amountMinor, numberLocale)}
        embeddedInAccount={embeddedInAccount}
        {...back}
      >
        <PhoneFirstDonationPayStepView {...props} />
      </DonationStepLayout>
    );
  }

  return null;
}
